import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useMediaQuery} from '@github-ui/use-media-query'
import {MeterIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Dialog, IconButton} from '@primer/react'
import {useRef, useState} from 'react'

import {TokenUsageInfo} from './TokenUsageInfo'

interface MeterButtonProps {
  /**
   * Chat mode reported in the token-usage telemetry event.
   */
  mode?: string
}

export function MeterButton({mode}: MeterButtonProps = {}) {
  const [isOpen, setIsOpen] = useState(false)
  const {reloadQuota} = useEntitlement()
  const isMobile = useMediaQuery('(max-width: 767.98px)')
  const anchorRef = useRef<HTMLButtonElement>(null)
  const openMeter = () => {
    setIsOpen(true)
    // Force a hard refresh so the popover always shows current spend,
    // bypassing the 5-minute entitlement staleTime and threshold guards.
    reloadQuota('meter_button', true)
    sendEvent('copilot_chat.activate', {
      target: 'SESSION_TOKEN_USAGE_METER',
      action: 'opened',
      mode,
    })
  }

  if (isMobile) {
    return (
      <>
        <IconButton
          ref={anchorRef}
          icon={MeterIcon}
          type="button"
          aria-label="View token usage"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          variant="invisible"
          size="medium"
          onClick={openMeter}
        />
        {isOpen && (
          <Dialog
            title="Token usage"
            position={{narrow: 'fullscreen'}}
            returnFocusRef={anchorRef}
            onClose={() => setIsOpen(false)}
          >
            <Dialog.Body style={{paddingTop: 0}}>
              <TokenUsageInfo />
            </Dialog.Body>
          </Dialog>
        )}
      </>
    )
  }

  return (
    <AnchoredOverlay
      anchorRef={anchorRef}
      open={isOpen}
      onOpen={openMeter}
      onClose={() => setIsOpen(false)}
      renderAnchor={props => (
        <IconButton
          icon={MeterIcon}
          {...props}
          type="button"
          aria-label="View token usage"
          aria-haspopup="dialog"
          variant="invisible"
          size="medium"
        />
      )}
      side="outside-top"
      align="center"
      anchorOffset={22}
      alignmentOffset={4}
      overlayProps={{role: 'dialog', 'aria-label': 'Token usage'}}
      variant={{regular: 'anchored', narrow: 'fullscreen'}}
      width="medium"
    >
      <TokenUsageInfo />
    </AnchoredOverlay>
  )
}
